/**
 * Shared password validation rules for auth surfaces.
 *
 * Used by RegisterPage, ResetPasswordPage, and ProfilePage so they all enforce
 * the same client-side rules. The server enforces these too via Better Auth's
 * `emailAndPassword.minPasswordLength` + its own password validators — if the
 * server's rules drift from this list, the user will get a confusing server
 * error after passing client-side checks. Keep these in sync.
 */

export interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

export const passwordRequirements: PasswordRequirement[] = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'One number', test: (p) => /\d/.test(p) },
];

/** True when every requirement passes. Useful for submit-button enable state. */
export function isPasswordValid(password: string): boolean {
  return passwordRequirements.every((r) => r.test(password));
}

/**
 * Returns the first unmet requirement's label as an error string, or null when
 * the password passes all checks. Use for surfacing a single specific reason
 * instead of "doesn't meet requirements" generic text.
 */
export function firstPasswordRequirementError(password: string): string | null {
  for (const req of passwordRequirements) {
    if (!req.test(password)) return req.label;
  }
  return null;
}
